import { FC, ReactNode, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Tooltip } from '@mui/material';
import { Stack } from '@mui/system';

import ProviderAmazonFBA from '@/shared/types/fulfilment/providerAmazonFBA.type';

import FulfilmentProviderDetails from '../types/fulfilmentProviderDetails.type';

type FormKey = FulfilmentProviderDetails & ProviderAmazonFBA;

const tooltipByField: {
  [key in keyof FormKey]: string;
} = {
  provider_name: 'fulfilmentProviders:providerNameHint',
  provider_description: 'fulfilmentProviders:providerDescriptionHint',
  fulfilment_provider_key: 'fulfilmentProviders:providerKeyHint',
  is_auto_fulfil: 'fulfilmentProviders:autoFulfilHint',
  is_force_inventory_sync: 'fulfilmentProviders:forceInventorySyncHint',
  fulfilment_delay_time: 'fulfilmentProviders:fulfilmentDelayTimeHint',
  fulfilment_update_interval:
    'fulfilmentProviders:fulfilmentUpdateIntervalHint',
  market_place_id: 'fulfilmentProviders:marketPlaceIdHint',
  refresh_token: 'fulfilmentProviders:refreshTokenHint',
  lwa_app_id: 'fulfilmentProviders:lwaAppIdHint',
  lwa_client_secret: 'fulfilmentProviders:lwaClientSecretHint',
  aws_secret_key: 'fulfilmentProviders:awsSecretKeyHint',
  aws_access_key: 'fulfilmentProviders:awsAccessKeyHint',
  role_arn: 'fulfilmentProviders:roleArnHint',
};

type FormFieldWithTooltipProps = {
  field: ReactNode;
};

const FormFieldWithTooltip: FC<FormFieldWithTooltipProps> = ({ field }) => {
  const { t } = useTranslation(['fulfilmentProviders', 'common']);

  if (!isValidElement(field)) return null;

  const { name } = field.props as { name: keyof FormKey };

  return (
    <Stack direction='row' alignItems='center' spacing={1} useFlexGap>
      {field}

      <Tooltip
        title={t(tooltipByField[name])}
        placement='top'
        tabIndex={-1}
        arrow
        PopperProps={{
          sx: { maxWidth: 250 },
        }}
      >
        <IconButton>
          <InfoOutlinedIcon sx={{ width: 16, height: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default FormFieldWithTooltip;
